// src/hooks/useProducts.ts
import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabaseClient';

export interface Product {
  id: string;
  name: string;
  category: string;
  subcategory?: string;
  material: string;
  thickness: number;
  price_per_m2: number;
  min_order: number;
  max_width: number;
  max_height: number;
  max_depth?: number;
  description?: string;
  images: string[];
  tags: string[];
  is_active: boolean;
  popularity: number;
  created_at: string;
  updated_at?: string;
  multiplier?: number;
  waste?: number;
  // Parametry specyficzne dla różnych typów produktów
  product_params?: {
    shelves?: number;
    partitions?: number;
    ledLength?: number;
    hookRows?: number;
    hookCols?: number;
    pockets?: number;
    kasetonType?: 'plexi' | 'dibond';
    hasTopper?: boolean;
    hasLid?: boolean;
  };
}

export function useProducts() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setProducts(data || []);
    } catch (err) {
      console.error('Error fetching products:', err);
      setError(err instanceof Error ? err.message : 'Błąd pobierania produktów');
    } finally {
      setLoading(false);
    }
  };

  const createProduct = async (productData: Omit<Product, 'id' | 'created_at'>): Promise<Product | null> => {
    try {
      const { data, error } = await supabase
        .from('products')
        .insert([{
          ...productData,
          updated_at: new Date().toISOString()
        }])
        .select()
        .single();

      if (error) throw error;
      
      await fetchProducts(); // Odśwież listę
      return data;
    } catch (err) {
      console.error('Error creating product:', err);
      setError(err instanceof Error ? err.message : 'Błąd tworzenia produktu');
      return null;
    }
  };

  const updateProduct = async (productId: string, updates: Partial<Product>): Promise<Product | null> => {
    try {
      const { data, error } = await supabase
        .from('products')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', productId)
        .select()
        .single();

      if (error) throw error;
      
      await fetchProducts(); // Odśwież listę
      return data;
    } catch (err) {
      console.error('Error updating product:', err);
      setError(err instanceof Error ? err.message : 'Błąd aktualizacji produktu');
      return null;
    }
  };

  const deleteProduct = async (productId: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('products')
        .delete()
        .eq('id', productId);

      if (error) throw error;
      
      await fetchProducts(); // Odśwież listę
      return true;
    } catch (err) {
      console.error('Error deleting product:', err);
      setError(err instanceof Error ? err.message : 'Błąd usuwania produktu');
      return false;
    }
  };

  const duplicateProduct = async (productId: string): Promise<Product | null> => {
    try {
      const product = products.find(p => p.id === productId);
      if (!product) throw new Error('Produkt nie znaleziony');

      const { id, created_at, updated_at, ...productData } = product;
      const newProduct = await createProduct({
        ...productData,
        name: `${product.name} (kopia)`,
        is_active: true
      });

      return newProduct;
    } catch (err) {
      console.error('Error duplicating product:', err);
      setError(err instanceof Error ? err.message : 'Błąd duplikowania produktu');
      return null;
    }
  };

  const searchProducts = async (query: string): Promise<Product[]> => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .or(`name.ilike.%${query}%,tags.cs.{${query}}`)
        .order('popularity', { ascending: false })
        .limit(10);

      if (error) throw error;
      return data || [];
    } catch (err) {
      console.error('Error searching products:', err);
      return [];
    }
  };

  return {
    products,
    loading,
    error,
    createProduct,
    updateProduct,
    deleteProduct,
    duplicateProduct,
    searchProducts,
    refreshProducts: fetchProducts
  };
}
